import { format } from 'date-fns';
import { Ban, Check } from 'lucide-react';
import { fetchChampionshipTeams } from 'modules/championship/store/championship/actions';
import { championshipSettingsSelector } from 'modules/championship/store/championship/selectors';
import { AnnouncedTeamStatus } from 'modules/teams/domain/enums/announced-team-status';
import { announceNewTeam, upsertChampionshipTeamApplication } from 'modules/teams/store/championship-teams/actions';
import { useLazyGetTeamByIdQuery } from 'modules/teams/store/teams-api';
import { useUnclaimedTeams } from 'modules/teams/ui/hooks/use-unclaimed-teams';
import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { AppDispatch } from 'store/store';
import UDIconButton from 'ud-ui/components/icon-button';
import UDText from 'ud-ui/components/text';
import ChampionshipTeamCardHeader from '../team-card/championship-team-card-header';
import { CardContainer } from '../team-card/styles';

type UnclaimedTeamsListProps = {
  championshipId: number;
  /** Команды, уже привязанные к этому турниру в любом статусе — их из выдачи исключаем. */
  excludeTeamIds: number[];
};

/**
 * Список команд организации, ещё не привязанных к турниру — показывается вместо
 * пустого состояния вкладки «Незаявленные», когда добавлять пока нечего.
 *
 * «Заявить»/«Отклонить» здесь сразу и привязывают команду к турниру, и решают её судьбу
 * целиком — быстрый путь, когда заявлять/отклонять по игрокам не нужно. Клик по самой
 * карточке — путь для точечного отбора: привязывает команду (как раньше делала
 * `AddTeamApplicationForm`) и сразу открывает её заявку модалкой, где игроков заявляют
 * по одному. Дата и число игроков берутся из того же `listTeams` — он отдаёт `createdAt` и
 * состав ровно настолько, чтобы посчитать реальных игроков (`isReal`); полные детали
 * (позиции, номера) при решении по команде всё равно дотягиваются отдельным запросом.
 */
const UnclaimedTeamsList = ({ championshipId, excludeTeamIds }: UnclaimedTeamsListProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const [fetchTeam] = useLazyGetTeamByIdQuery();
  const [actingTeamId, setActingTeamId] = useState<number | null>(null);
  const settings = useSelector(championshipSettingsSelector(championshipId));
  const maxPlayers = settings?.applicationMaxPlayers ?? 28;

  const { availableTeams } = useUnclaimedTeams(excludeTeamIds);

  const handleDecide = useCallback(
    async (teamId: number, status: AnnouncedTeamStatus) => {
      setActingTeamId(teamId);

      try {
        const detailTeam = await fetchTeam(teamId).unwrap();

        if (!detailTeam) {
          toast.error('Команда не найдена');
          return;
        }

        const players = detailTeam.players.filter((player) => !player.isReal);

        if (players.length === 0) {
          toast.error('Необходимо добавить участников в команду.');
          return;
        }

        if (status === AnnouncedTeamStatus.ACCEPTED && players.length > maxPlayers) {
          toast.error(
            `Превышено допустимое количество игроков в команде. По условиям турнира максимальное количество игроков в команде: ${maxPlayers}. При заявлении команды все игроки, находящиеся в ней, будут заявлены. Для заявления конкретных игроков перейдите в заявку команды, кликнув на неё.`,
            { autoClose: 1000 * 45 },
          );
          return;
        }

        await dispatch(
          announceNewTeam({
            team: { id: detailTeam.id, name: detailTeam.name, shortName: detailTeam.shortName, logo: detailTeam.logo, players },
            championshipId,
          }),
        ).unwrap();

        await dispatch(upsertChampionshipTeamApplication({ teamId, status, championshipId })).unwrap();

        toast.success(
          status === AnnouncedTeamStatus.ACCEPTED ? 'Все игроки команда успешно заявлены.' : 'Все заявки игроков команда успешно отклонены.',
        );
        dispatch(fetchChampionshipTeams(championshipId));
      } catch (error) {
        const message = error instanceof Error ? error.message : '';
        if (message.includes('limit exceeded')) {
          const limit = message.match(/(\d+)/g)?.[0] ?? '-';
          toast.error(
            `Превышен лимит заявок на участие в чемпионате. По условиям доступно заявок на команду: ${limit}. При заявлении команды все игроки, находящиеся в ней, будут заявлены. Вероятно кол-во игроков в команде превышает допустимое кол-во заявок установленное в условиях турнира. Для заявления конкретных игроков перейдите в заявку команды, кликнув на неё.`,
            { autoClose: 1000 * 45 },
          );
        } else {
          toast.error('Не удалось добавить команду');
        }
      } finally {
        setActingTeamId(null);
      }
    },
    [championshipId, dispatch, fetchTeam, maxPlayers],
  );

  const handleCardClick = useCallback(
    async (teamId: number) => {
      setActingTeamId(teamId);

      try {
        const detailTeam = await fetchTeam(teamId).unwrap();

        if (!detailTeam) {
          toast.error('Команда не найдена');
          return;
        }

        await dispatch(
          announceNewTeam({
            team: {
              id: detailTeam.id,
              name: detailTeam.name,
              shortName: detailTeam.shortName,
              logo: detailTeam.logo,
              players: detailTeam.players.filter((player) => !player.isReal),
            },
            championshipId,
          }),
        ).unwrap();

        navigate(String(teamId));
      } catch {
        toast.error('Не удалось добавить команду');
      } finally {
        setActingTeamId(null);
      }
    },
    [championshipId, dispatch, fetchTeam, navigate],
  );

  if (availableTeams.length === 0) {
    return (
      <div className="flex-center h-full">
        <UDText type="body-text" className="text-surfaceRomanSilver30">
          Список пуст
        </UDText>
      </div>
    );
  }

  return (
    <div>
      {availableTeams.map((team) => {
        const isActing = actingTeamId === team.id;
        const realPlayersCount = team.players.filter((player) => !player.isReal).length;
        // `team.createdAt` уже отформатирован в дату-строку мапером (`getNormalizedDate`),
        // а не ISO-таймстамп — повторный `new Date(...)` + `format` на нём падал с
        // «Invalid time value». Показываем как есть, с сегодняшней датой как запасной.
        const teamDate = team.createdAt || format(new Date(), 'dd.MM.yyyy');

        return (
          <CardContainer key={team.id} className="mb-2 last:mb-0" style={{ cursor: 'pointer' }} onClick={() => handleCardClick(team.id)}>
            <ChampionshipTeamCardHeader
              isExpanded={false}
              teamName={team.name}
              shortName={team.shortName}
              teamLogo={team.logo?.url}
              actions={
                <div className="flex items-center justify-between" style={{ flexBasis: '50%' }}>
                  <UDText type="subhead" style={{ flexBasis: '30%' }}>
                    {teamDate}
                  </UDText>
                  <div className="flex grow items-center justify-between">
                    <UDText type="subhead" className="team-players-count">
                      {t('teams.playersCount', { count: realPlayersCount })}
                    </UDText>
                    <div className="interactive flex items-center justify-end" style={{ width: 92, minWidth: 92, gap: 20 }}>
                      <UDIconButton
                        icon={<Check />}
                        variant="success"
                        tooltip="Заявить"
                        disabled={isActing}
                        onClick={(event: React.MouseEvent) => {
                          event.stopPropagation();
                          handleDecide(team.id, AnnouncedTeamStatus.ACCEPTED);
                        }}
                      />
                      <UDIconButton
                        icon={<Ban />}
                        variant="danger"
                        tooltip="Отклонить"
                        disabled={isActing}
                        onClick={(event: React.MouseEvent) => {
                          event.stopPropagation();
                          handleDecide(team.id, AnnouncedTeamStatus.DECLINED);
                        }}
                      />
                    </div>
                  </div>
                </div>
              }
            />
          </CardContainer>
        );
      })}
    </div>
  );
};

export default UnclaimedTeamsList;
